import { z } from 'zod';
import { IsoDateTime, QueryBoolean, Uuid } from './common.js';
/**
 * Encaissements (DOMAIN_ARCHITECTURE §7, DATA_MODEL §3.7).
 *
 * Le module Paiements complet arrive au sprint 2.6 : seuls les schémas
 * nécessaires à l'avance de confirmation (R-013, D-020/D-022) sont figés ici.
 */
export const PaymentMethod = z.enum([
    'CASH',
    'MOBILE_MONEY',
    'BANK_TRANSFER',
    'CHEQUE',
    'CARD',
    'OTHER',
]);
/** Opérateur ou établissement à l'origine du règlement (mobile money, banque). */
export const PaymentProvider = z.enum([
    'ORANGE_MONEY',
    'MOOV_MONEY',
    'WAVE',
    'CORIS_MONEY',
    'CORIS_BANK',
    'ECOBANK',
    'UBA',
    'OTHER',
]);
export const PaymentStatus = z.enum(['RECORDED', 'CONFIRMED', 'REVERSED']);
/** Résumé d'un encaissement, rendu par `POST /reservations/:id/deposits`. */
export const Payment = z.object({
    id: Uuid,
    reference: z.string(),
    method: PaymentMethod,
    provider: PaymentProvider.nullable(),
    amount: z.number(),
    currencyCode: z.string().length(3),
    paidAt: IsoDateTime,
    transactionReference: z.string().nullable(),
    status: PaymentStatus,
    customerId: Uuid.nullable(),
    reservationId: Uuid.nullable(),
    notes: z.string().nullable(),
    recordedByName: z.string().nullable(),
    createdAt: IsoDateTime,
});
/**
 * Avance de confirmation : montant libre strictement positif, sans pourcentage
 * ni minimum (D-022). `paidAt` par défaut = maintenant.
 */
export const RecordDepositRequest = z.object({
    amount: z.number().positive('Le montant de l’avance doit être strictement positif'),
    method: PaymentMethod,
    provider: PaymentProvider.nullable().optional(),
    transactionReference: z.string().max(80).nullable().optional(),
    paidAt: IsoDateTime.optional(),
    notes: z.string().max(1000).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Sprint 2.6 — module Paiements complet (E-11, FR-PAY-01…04, R-008)
// ---------------------------------------------------------------------------
export const PaymentKind = z.enum(['DEPOSIT', 'PAYMENT', 'REFUND']);
export const PaymentAllocation = z.object({
    id: Uuid,
    invoiceId: Uuid.nullable(),
    invoiceNumber: z.string().nullable(),
    reservationId: Uuid.nullable(),
    reservationReference: z.string().nullable(),
    /** Commande POS réglée (H2, D-H10). */
    orderId: Uuid.nullable().optional(),
    orderReference: z.string().nullable().optional(),
    amount: z.number(),
});
/** Paiement détaillé (liste et fiche). */
export const PaymentDetail = Payment.extend({
    kind: PaymentKind,
    propertyId: Uuid,
    customerName: z.string().nullable(),
    chequeNumber: z.string().nullable(),
    bankName: z.string().nullable(),
    allocations: z.array(PaymentAllocation),
    /** Montant non encore affecté à une facture ou une réservation. */
    unallocatedAmount: z.number(),
    refundedAmount: z.number(),
    reversal: z
        .object({
        reason: z.string(),
        reversedAt: IsoDateTime,
        reversedByName: z.string().nullable(),
        reversalPaymentId: Uuid.nullable(),
    })
        .nullable(),
    receiptDocumentId: Uuid.nullable(),
    proofDocumentId: Uuid.nullable(),
    cashSessionId: Uuid.nullable(),
    version: z.number().int().nonnegative(),
    updatedAt: IsoDateTime,
});
export const AllocationInput = z
    .object({
    invoiceId: Uuid.optional(),
    reservationId: Uuid.optional(),
    /** Commande POS (H2, D-H10). */
    orderId: Uuid.optional(),
    amount: z.number().positive(),
})
    .refine((v) => [v.invoiceId, v.reservationId, v.orderId].filter(Boolean).length === 1, {
    message: 'Une allocation vise soit une facture, soit une réservation, soit une commande',
});
/** Enregistrement d'un paiement (facture et/ou réservation), idempotent via Idempotency-Key. */
export const RecordPaymentRequest = z.object({
    customerId: Uuid.optional(),
    amount: z.number().positive(),
    method: PaymentMethod,
    provider: PaymentProvider.nullable().optional(),
    transactionReference: z.string().max(80).nullable().optional(),
    chequeNumber: z.string().max(40).nullable().optional(),
    bankName: z.string().max(80).nullable().optional(),
    paidAt: IsoDateTime.optional(),
    notes: z.string().max(1000).nullable().optional(),
    /** Si absent avec `invoiceId`/`reservationId` implicite, le serveur alloue tout au document visé. */
    allocations: z.array(AllocationInput).default([]),
    proofDocumentId: Uuid.nullable().optional(),
});
export const UpdatePaymentAllocationsRequest = z.object({
    allocations: z.array(AllocationInput),
    version: z.number().int().nonnegative(),
});
/** Annulation d'un paiement : écriture compensatoire, jamais de suppression (R-006). */
export const VoidPaymentRequest = z.object({
    reason: z.string().min(5).max(500),
    version: z.number().int().nonnegative(),
});
export const RefundRequest = z.object({
    amount: z.number().positive(),
    method: PaymentMethod,
    provider: PaymentProvider.nullable().optional(),
    transactionReference: z.string().max(80).nullable().optional(),
    reason: z.string().min(5).max(500),
    refundedAt: IsoDateTime.optional(),
    version: z.number().int().nonnegative(),
});
export const ListPaymentsQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(512).optional(),
    q: z.string().max(100).optional(),
    method: PaymentMethod.optional(),
    status: PaymentStatus.optional(),
    kind: PaymentKind.optional(),
    customerId: Uuid.optional(),
    invoiceId: Uuid.optional(),
    reservationId: Uuid.optional(),
    orderId: Uuid.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    unallocatedOnly: QueryBoolean.optional(),
});
/** Reçu de paiement (PDF) — numéroté REC/AAAA/NNNNN. */
export const Receipt = z.object({
    paymentId: Uuid,
    number: z.string(),
    documentId: Uuid.nullable(),
    issuedAt: IsoDateTime,
});
/** Envoi d'un document de facturation ou d'un reçu par e-mail. */
export const SendDocumentRequest = z.object({
    channels: z.array(z.enum(['EMAIL', 'WHATSAPP'])).min(1),
    to: z.string().max(200).optional(),
    message: z.string().max(2000).optional(),
});
/** Résultat d'un envoi : canaux effectivement servis et destinataire retenu. */
export const SendDocumentResult = z.object({
    channels: z.array(z.enum(['EMAIL', 'WHATSAPP'])),
    to: z.string(),
});
//# sourceMappingURL=payments.js.map